import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { BaseChartModel } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { ChartDataPointBase } from "@/Patient/Models/GrowthCharts/ChartData";
import { GrowthChartsInterpolation } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { Age0To2LengthForAge, Age0To2WeightForAge, Age0To2WeightForLength } from "./ChartConfigurations/Age0To2GrowthCharts";
import { Age0To5HeadCircumference } from "./ChartConfigurations/Age0To5GrowthCharts";
import { Age2To19BodyMassIndex, Age2To19HeightForAge, Age2To19WeightForAge } from "./ChartConfigurations/Age2To19GrowthCharts";
import { FentonHeadCircumference, FentonLengthForAge, FentonWeightForAge } from "./ChartConfigurations/FentonGrowthCharts";
import { BloodPressureVitalSign, BMIVitalSign, HeartRateVitalSign, HeightVitalSign, WeightVitalSign } from "./ChartConfigurations/VitalSignChartModel";

interface GrowthChartsData extends ChartDataPointBase {
    id: string;
    date: Date;
    medicalItemId: string;
    vitalSignValue: string;
    chronologicalAge: number;
    chronologicalPercentile: string;
    correctedAge?: number;
    correctedPercentile?: string;
}

class GrowthChartSelectable {
    public readonly chart: BaseChartModel;
    public isChecked: boolean;

    public constructor(chart: BaseChartModel) {
        this.chart = chart;
        this.isChecked = chart.isDisplayedByDefault();
    }
}

function getSelectableCharts(
    patient: Individual,
    vitals: Immutable.List<MedicalItemVitalSign>,
    chartInterpolations: { [key: string]: GrowthChartsInterpolation[] },
    zScores: { [key: number]: { [key: number]: number } },
    unit: UnitSystem
): GrowthChartSelectable[] {
    vitals = vitals.sortBy(x => x.PharmaStartDate).toList();
    const charts = [
        new FentonWeightForAge(patient, vitals, chartInterpolations, zScores, unit),
        new Age0To2WeightForAge(patient, vitals, chartInterpolations, zScores, unit),
        new Age2To19WeightForAge(patient, vitals, chartInterpolations, zScores, unit),
        new FentonLengthForAge(patient, vitals, chartInterpolations, zScores, unit),
        new Age0To2LengthForAge(patient, vitals, chartInterpolations, zScores, unit),
        new Age2To19HeightForAge(patient, vitals, chartInterpolations, zScores, unit),
        new FentonHeadCircumference(patient, vitals, chartInterpolations, zScores, unit),
        new Age0To5HeadCircumference(patient, vitals, chartInterpolations, zScores, unit),
        new Age0To2WeightForLength(patient, vitals, chartInterpolations, zScores, unit),
        new Age2To19BodyMassIndex(patient, vitals, chartInterpolations, zScores, unit),
        new WeightVitalSign(patient, vitals, unit),
        new HeightVitalSign(patient, vitals, unit),
        new BMIVitalSign(patient, vitals, unit),
        new BloodPressureVitalSign(patient, vitals, unit),
        new HeartRateVitalSign(patient, vitals, unit)
    ].filter(chart => chart.isCompatible());

    return charts.map(chart => new GrowthChartSelectable(chart));
}

export { getSelectableCharts, GrowthChartsData, GrowthChartSelectable };
